import React, { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ChevronDown, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface StatusMultiSelectProps {
  options: Array<{ label: string; key: string }>;
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  /** Max height class for the scrollable options list */
  listMaxHeightClassName?: string;
}

const StatusMultiSelect: React.FC<StatusMultiSelectProps> = ({
  options,
  value = [],
  onChange,
  placeholder = "Select Status",
  className,
  listMaxHeightClassName = "max-h-60",
}) => {
  const [open, setOpen] = useState(false);

  const handleToggle = (optionKey: string) => {
    const newValue = value.includes(optionKey)
      ? value.filter((v) => v !== optionKey)
      : [...value, optionKey];
    onChange(newValue);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log("clicked");
    setOpen(false);
    onChange([]);
  };

  const selectedLabels = options
    .filter((opt) => value.includes(opt.key))
    .map((opt) => opt.label);

  const isActive = value.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className={cn(
            "max-h-8 rounded-lg font-medium text-sm px-3 ",
            isActive
              ? "bg-primary-dark text-white hover:bg-primary-dark border-primary-dark"
              : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:text-black",
            className,
          )}
        >
          <span className="truncate">
            {value.length === 0
              ? placeholder
              : value.length === 1
                ? selectedLabels[0]
                : `${placeholder} (${value.length}) `}
          </span>
          <div className="flex items-center gap-1 ml-0">
            {isActive && false && (
              <button
              type="button"
              className="inline-flex"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClear(e);
              }}
            >
              <X className="h-4 w-4 pointer-events-none" />
            </button>
            )}
            <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0" align="start">
        <div className="p-2">
          {options.length === 0 ? (
            <p className="px-1 py-2 text-center text-sm text-muted-foreground">
              No {placeholder?.toLowerCase() || "options"} available
            </p>
          ) : (
            <div
              className={cn(
                "space-y-1 overflow-y-auto overscroll-contain",
                listMaxHeightClassName,
              )}
            >
              {options.map((option) => {
                const isChecked = value.includes(option.key);
                return (
                  <div
                    key={option.key}
                    className="flex items-center space-x-2 py-1.5 px-2 rounded-md hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleToggle(option.key)}
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={() => handleToggle(option.key)}
                      className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                    />
                    <Label
                      className={cn(
                        "text-sm font-normal cursor-pointer flex-1",
                        isChecked ? "text-gray-900" : "text-gray-700",
                      )}
                    >
                      {option.label}
                    </Label>
                  </div>
                );
              })}
            </div>
          )}
          {value.length > 0 && (
            <div className="mt-3 pt-3 border-t flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {value.length} selected
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => onChange([])}
              >
                Clear all
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default StatusMultiSelect;
